import { useState, type FormEvent } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Input } from '@/Components/ui/input';
import { Textarea } from '@/Components/ui/textarea';
import { Link } from 'react-router-dom';
import { siteConfig } from '@/config/site';
import {
    Clock,
    Mail,
    MessageSquare,
    Phone,
    MapPin,
    HelpCircle,
    DollarSign,
    Monitor,
    FileText,
    ArrowRight,
    Send,
} from 'lucide-react';

type FormStatus = 'idle' | 'sending' | 'sent';

interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    subject: string;
    message: string;
}

const subjectOptions = [
    { value: '', label: 'Select a subject' },
    { value: 'general', label: 'General Inquiry' },
    { value: 'funding', label: 'Funding Programs' },
    { value: 'platform', label: 'Platform Support' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'other', label: 'Other' },
];

const stats = [
    { icon: Clock, label: 'Response Time', value: '<2hrs' },
    { icon: MessageSquare, label: 'Support', value: '24/7' },
    { icon: Send, label: 'Satisfaction', value: '98%' },
];

const contactInfoCards = [
    {
        icon: Clock,
        title: 'Premium Support',
        lines: ['Available 24/5 during market hours', 'Average response time: <2 hours'],
    },
    {
        icon: Mail,
        title: 'Email Support',
        lines: ['support@teffunded.com', 'sales@teffunded.com', 'partners@teffunded.com'],
    },
    {
        icon: MessageSquare,
        title: 'Live Chat',
        lines: ['Instant support via our website', 'Available during business hours'],
    },
    {
        icon: Phone,
        title: 'Phone Support',
        lines: ['+971 54 747 2193', 'Monday - Friday'],
    },
];

const offices = [
    {
        city: 'Saint Lucia',
        address: 'Top Floor, Rodney Court Building, Rodney Bay, Gros Islet',
    },
    {
        city: 'Dubai, UAE',
        address: 'Office #210, Al-Nasar Sports Club',
    },
];

const quickHelpLinks = [
    {
        icon: HelpCircle,
        title: 'FAQ',
        description: 'Find answers to common questions about our services.',
        href: '/faq',
    },
    {
        icon: DollarSign,
        title: 'Funding Info',
        description: 'Explore our funding programs and challenge details.',
        href: '/funding/overview',
    },
    {
        icon: Monitor,
        title: 'Platform Help',
        description: 'Get help with platform setup, tools, and features.',
        href: '/platform/overview',
    },
    {
        icon: FileText,
        title: 'Legal Docs',
        description: 'Review our terms, policies, and legal documentation.',
        href: '/terms-and-conditions',
    },
];

export default function Contact() {
    const [formData, setFormData] = useState<FormData>({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: '',
    });
    const [status, setStatus] = useState<FormStatus>('idle');

    function handleChange(
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    }

    function handleSubmit(e: FormEvent) {
        e.preventDefault();
        setStatus('sending');
        setTimeout(() => {
            setStatus('sent');
            setFormData({ firstName: '', lastName: '', email: '', subject: '', message: '' });
            setTimeout(() => setStatus('idle'), 3000);
        }, 1500);
    }

    return (
        <AppLayout title={`Contact ${siteConfig.brand.name} - Get in Touch`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <div className="text-center">
                    <FadeIn>
                        <Badge className="mb-6">Get in Touch</Badge>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            Contact <span className="text-gradient">{siteConfig.brand.name}</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-12">
                            Have questions about our prop trading programs? Our team is here to help you
                            get started on your trading journey.
                        </p>
                    </FadeIn>
                    <FadeIn delay={0.3}>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto">
                            {stats.map((stat) => (
                                <div
                                    key={stat.label}
                                    className="flex flex-col items-center gap-2 rounded-xl border border-border bg-card/80 backdrop-blur-sm p-5 hover:border-primary/20 transition-colors duration-200"
                                >
                                    <div className="h-10 w-10 rounded-lg bg-primary/10 border border-primary/10 flex items-center justify-center">
                                        <stat.icon className="h-5 w-5 text-primary" />
                                    </div>
                                    <span className="text-2xl font-bold text-foreground">
                                        {stat.value}
                                    </span>
                                    <span className="text-sm text-muted-foreground">{stat.label}</span>
                                </div>
                            ))}
                        </div>
                    </FadeIn>
                </div>
            </Section>

            {/* Contact Form & Info */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Get in"
                        highlight="Touch"
                        description="Fill out the form below and our team will get back to you as soon as possible."
                    />
                </FadeIn>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                    {/* Form */}
                    <FadeIn delay={0.1}>
                        <Card className="rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors duration-200">
                            <CardContent className="p-6">
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1.5">
                                                First Name
                                            </label>
                                            <Input
                                                name="firstName"
                                                placeholder="John"
                                                value={formData.firstName}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-foreground mb-1.5">
                                                Last Name
                                            </label>
                                            <Input
                                                name="lastName"
                                                placeholder="Doe"
                                                value={formData.lastName}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1.5">
                                            Email
                                        </label>
                                        <Input
                                            name="email"
                                            type="email"
                                            placeholder="john@example.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1.5">
                                            Subject
                                        </label>
                                        <select
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            required
                                            className="flex h-10 w-full rounded-lg border border-border bg-background/50 px-4 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50 transition-all duration-200"
                                        >
                                            {subjectOptions.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-1.5">
                                            Message
                                        </label>
                                        <Textarea
                                            name="message"
                                            placeholder="Tell us how we can help..."
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            rows={5}
                                        />
                                    </div>
                                    <Button
                                        type="submit"
                                        size="lg"
                                        className="w-full"
                                        disabled={status === 'sending'}
                                    >
                                        {status === 'sending' ? (
                                            'Sending...'
                                        ) : status === 'sent' ? (
                                            'Message Sent!'
                                        ) : (
                                            <>
                                                <Send className="mr-2 h-4 w-4" />
                                                Send Message
                                            </>
                                        )}
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    </FadeIn>

                    {/* Info Cards */}
                    <StaggerChildren className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {contactInfoCards.map((card) => (
                            <motion.div key={card.title} variants={staggerItem}>
                                <Card className="h-full rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors duration-200">
                                    <CardContent className="p-6">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="h-10 w-10 rounded-lg bg-primary/10 border border-primary/10 flex items-center justify-center">
                                                <card.icon className="h-5 w-5 text-primary" />
                                            </div>
                                            <h3 className="font-semibold text-foreground">
                                                {card.title}
                                            </h3>
                                        </div>
                                        <div className="space-y-1">
                                            {card.lines.map((line) => (
                                                <p
                                                    key={line}
                                                    className="text-sm text-muted-foreground"
                                                >
                                                    {line}
                                                </p>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </StaggerChildren>
                </div>
            </Section>

            {/* Global Offices */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Global"
                        highlight="Offices"
                        description="Visit us at one of our international office locations."
                    />
                </FadeIn>
                <StaggerChildren className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                    {offices.map((office) => (
                        <motion.div key={office.city} variants={staggerItem}>
                            <Card className="rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors duration-200">
                                <CardContent className="p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="h-10 w-10 shrink-0 rounded-lg bg-primary/10 border border-primary/10 flex items-center justify-center">
                                            <MapPin className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-foreground mb-1">
                                                {office.city}
                                            </h3>
                                            <p className="text-sm text-muted-foreground">
                                                {office.address}
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </StaggerChildren>
            </Section>

            {/* Quick Help */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Quick"
                        highlight="Help"
                        description="Browse our resources for instant answers and self-service support."
                    />
                </FadeIn>
                <StaggerChildren className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {quickHelpLinks.map((item) => (
                        <motion.div key={item.title} variants={staggerItem}>
                            <Link to={item.href}>
                                <Card className="h-full group cursor-pointer rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors duration-200">
                                    <CardContent className="p-6">
                                        <div className="h-10 w-10 rounded-lg bg-primary/10 border border-primary/10 flex items-center justify-center mb-4">
                                            <item.icon className="h-5 w-5 text-primary" />
                                        </div>
                                        <h3 className="font-semibold text-foreground mb-2">
                                            {item.title}
                                        </h3>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            {item.description}
                                        </p>
                                        <span className="inline-flex items-center text-sm font-medium text-primary group-hover:gap-2 transition-all duration-200">
                                            Learn More
                                            <ArrowRight className="ml-1 h-4 w-4" />
                                        </span>
                                    </CardContent>
                                </Card>
                            </Link>
                        </motion.div>
                    ))}
                </StaggerChildren>
            </Section>

            {/* CTA */}
            <Section className="bg-background">
                <FadeIn>
                    <div className="text-center rounded-2xl border border-border bg-card/80 backdrop-blur-sm p-12 lg:p-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            Ready to Start <span className="text-gradient">Trading?</span>
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                            Join thousands of funded traders and take the first step toward your
                            professional trading career with {siteConfig.brand.name}.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link to="/funding/overview">
                                <Button size="lg">
                                    Get Funded
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </Link>
                            <Link to="/faq">
                                <Button variant="outline" size="lg">
                                    View FAQ
                                </Button>
                            </Link>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
